<template>
  <div class="documents-page">
    <!-- account-scope requirements -->
    <div class="mb-2">
      <h6 class="mt-0">Documents requested by Swiss Finance Corporation</h6>
      <p v-if="accountRequirements.length" class="text-muted small">
        Please upload each of the documents listed below. Rejected documents need to be uploaded again.
      </p>
      <requirement-upload-list
        :requirements="accountRequirements"
        :can-upload="canUpload"
        empty-text="No account documents have been requested from you."
        @payload="$emit('payload', $event)"
      />

      <small v-if="personRequirementCount" class="text-muted d-block mt-50">
        <feather-icon icon="UsersIcon" size="12" class="mr-25" />
        {{ personRequirementCount }} document(s) relate to individual persons
        ({{ personOutstanding }} outstanding) — they are uploaded on the respective person's step.
      </small>
    </div>

    <div class="mb-2">
      <b-button variant="outline-secondary" size="sm" :disabled="!canUpload" @click="$refs.extraUpload.open(null)">
        <feather-icon icon="UploadIcon" size="14" class="mr-25" />
        Upload another document
      </b-button>
      <upload-note-modal ref="extraUpload" @uploaded="$emit('payload', $event)" />
    </div>

    <!-- uploads table -->
    <h6>Your uploads</h6>
    <b-table
      :items="documents"
      :fields="documentFields"
      show-empty
      empty-text="No documents uploaded yet."
      responsive
      class="documents-table"
    >
      <template #cell(FileName)="{ item }">
        <a href="#" @click.prevent="download(item)">{{ item.FileName }}</a>
        <small class="text-muted d-block">{{ formatSize(item.FileSize) }}</small>
      </template>
      <template #cell(RequirementName)="{ item }">
        <span v-if="item.RequirementName">{{ item.RequirementName }}</span>
        <span v-else-if="item.CustomerRequestId" class="text-muted">Provided by SFC</span>
        <span v-else class="text-muted">Additional document</span>
      </template>
      <template #cell(Note)="{ value }">
        <span v-if="value">{{ value }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(ReviewStatus)="{ item }">
        <b-badge :variant="'light-' + reviewVariant(item.ReviewStatus)">{{ statusLabel(item.ReviewStatus) }}</b-badge>
        <small v-if="item.ReviewStatus === 'Rejected' && item.ReviewReason" class="text-danger d-block">{{ item.ReviewReason }}</small>
      </template>
      <template #cell(created_at)="{ value }">{{ formatDate(value) }}</template>
      <template #cell(actions)="{ item }">
        <b-button variant="flat-primary" size="sm" @click="download(item)">
          <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
          Download
        </b-button>
      </template>
    </b-table>

    <customer-requests-card :customer-requests="customerRequests" class="mt-2 mb-0" @payload="$emit('payload', $event)" />
  </div>
</template>

<script>
// "Documents" wizard step: account-scope requirements, free uploads, the uploads
// table and the requests-to-SFC card.
import RequirementUploadList from "./RequirementUploadList.vue";
import UploadNoteModal from "./UploadNoteModal.vue";
import CustomerRequestsCard from "./CustomerRequestsCard.vue";
import { downloadDocument, formatSize, formatDate, reviewStatusVariant, statusLabel } from "./publicApi";

export default {
  name: "DocumentsPage",

  components: { RequirementUploadList, UploadNoteModal, CustomerRequestsCard },

  props: {
    requirements: { type: Array, default: () => [] },
    documents: { type: Array, default: () => [] },
    customerRequests: { type: Array, default: () => [] },
    canUpload: { type: Boolean, default: true },
  },

  data() {
    return {
      documentFields: [
        { key: "FileName", label: "File" },
        { key: "RequirementName", label: "For" },
        { key: "Note", label: "Note" },
        { key: "ReviewStatus", label: "Status" },
        { key: "created_at", label: "Uploaded" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },

  computed: {
    accountRequirements() {
      return this.requirements.filter((row) => Number(row.PersonId) === 0 || row.Scope === "Account");
    },
    personRequirements() {
      return this.requirements.filter((row) => Number(row.PersonId) !== 0 && row.Scope !== "Account");
    },
    personRequirementCount() {
      return this.personRequirements.length;
    },
    personOutstanding() {
      return this.personRequirements.filter((row) => row.Status === "Requested" || row.Status === "Rejected").length;
    },
  },

  methods: {
    formatSize,
    formatDate,
    statusLabel,

    reviewVariant(status) {
      return reviewStatusVariant[status] || "secondary";
    },

    download(item) {
      downloadDocument(item.id, item.FileName).catch(() => {
        this.$bvToast.toast("Download failed.", { title: "Error", variant: "danger", solid: true });
      });
    },
  },
};
</script>
